import { CalendarOutlined, EditFilled } from '@ant-design/icons'
import { addCommasToNumber, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { isDefined } from '@utils/general'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Form, InputNumber, Popconfirm, Popover, Switch } from 'antd'
import React, { useMemo, useState } from 'react'
import moment from 'moment'

export function ForecastOverrideCellRenderer({
  column,
  value,
  data,
  overrideVolume,
  deleteOverride,
  visiblePopoverId,
  setVisiblePopoverId,
  hasOverridePermission,
  storeTimeZone,
  isManifoldChild,
}) {
  const override = useMemo(() => {
    if (column !== 'sales' && column !== 'forecast') {
      return null
    }

    const sales_override = data?.sales_override
    const forecast_override = data?.forecast_override

    if (sales_override) {
      if (column === 'sales' && !moment(sales_override.date) >= moment(moment.now())) {
        return null
      }
    }

    return column === 'sales' ? sales_override : forecast_override
  }, [data, column])
  const [deletePopoverVisible, setDeletePopoverVisible] = useState(false)
  const [isExcluded, setIsExcluded] = useState(Boolean(override?.exclude_from_forecast))
  const [excludeForecastValue, setExcludeForecastValue] = useState(false)
  const [volumeOverrideValue, setVolumeOverrideValue] = useState()
  const isToday = useMemo(() => data?.type === 'today', [data])
  const title = useMemo(() => (column === 'sales' ? 'Sales' : 'Forecast'), [column])
  const hidePopover = useMemo(
    () => !hasOverridePermission || (column === 'forecast' && data.type === 'past') || (isToday && override),
    [hasOverridePermission, column, data, isToday, override]
  )

  const isOverrideReady = useMemo(() => {
    return isDefined(volumeOverrideValue) || Boolean(override?.exclude_from_forecast) !== isExcluded
  }, [volumeOverrideValue, isExcluded, data])
  const showDayForecast = isToday && column === 'forecast' && data.whole_day_forecast

  const buttonText = (
    <Horizontal className='override-wrapper' justifyContent='end'>
      <Texto apperance='white' align='right' weight={600} className='text-truncate'>
        {override?.event_id && <CalendarOutlined style={{ paddingRight: '0.5em' }} />}
        {addCommasToNumber(value, 0)}
        {showDayForecast && <span className='pl-1'>{`(${addCommasToNumber(data.whole_day_forecast, 0)})`}</span>}
      </Texto>

      {!isManifoldChild && (
        <EditFilled style={{ visibility: hidePopover ? 'hidden' : '' }} className='override-cell-edit pl-4' />
      )}
    </Horizontal>
  )

  const msg = data.type === 'past' ? `Enter Correct Sales` : 'Enter Forecast Override'
  const blurb = useMemo(() => {
    const now = new Date()
    const nowTime = formatters.TIME(now, storeTimeZone)
    if (data.type === 'today') {
      return (
        <Vertical>
          <Texto category='h6' appearance='medium' style={{ fontStyle: 'italic' }}>
            INTRADAY OVERRIDE
          </Texto>
          <Texto>This will only override the remaining forecast for the day:</Texto>
          <Texto weight='bold'> Now ({nowTime}) - 11:59 PM</Texto>
        </Vertical>
      )
    }
    if (data.type === 'past') {
      return 'Use this override if there is a discrepancy in sales.'
    }
    return 'Use this override to adjust the Forecasted Sales.'
  }, [data?.type])
  const popContent = (
    <Vertical>
      {!(data.type !== 'past' && column === 'sales') && (
        <>
          <Horizontal className='p-2'>{blurb}</Horizontal>
          <Horizontal justifyContent='space-between' verticalCenter className='p-2'>
            <Vertical alignItems='start' justifyContent='center' flex={1}>
              <Texto>{column === 'sales' ? 'Sales' : 'Forecasted Sales'}:</Texto>
            </Vertical>
            <Vertical alignItems='start' justifyContent='center' flex={1}>
              <InputNumber
                min={0}
                style={{ width: '150px' }}
                placeholder={msg}
                onChange={(val) => setVolumeOverrideValue(val)}
                value={volumeOverrideValue}
              />
            </Vertical>
          </Horizontal>
        </>
      )}

      {column !== 'forecast' && (
        <Horizontal verticalCenter className='p-2' justifyContent='space-between'>
          <Vertical alignItems='start' justifyContent='center' flex={1}>
            <Texto>Exclude from Forecast:</Texto>
          </Vertical>
          <Vertical alignItems='start' justifyContent='center' flex={1}>
            <Switch className='warning-switch' checked={isExcluded} onChange={(val) => setIsExcluded(val)} />
          </Vertical>
        </Horizontal>
      )}

      {column === 'forecast' && (
        <Horizontal verticalCenter className='p-2' justifyContent='space-between'>
          <Vertical alignItems='start' justifyContent='center' flex={4}>
            <Texto>Exclude Sales from Forecast</Texto>
          </Vertical>
          <Vertical alignItems='start' justifyContent='center' flex={1}>
            <Switch checked={excludeForecastValue} onChange={(val) => setExcludeForecastValue(val)} />
          </Vertical>
        </Horizontal>
      )}

      <hr className='forecast-hr' />
      <Horizontal justifyContent='space-between' className='p-2'>
        <GraviButton
          className='ghost-gravi-button'
          buttonText='Cancel'
          onClick={() => {
            setVolumeOverrideValue(0)
            setIsExcluded(false)
            setVisiblePopoverId(null)
          }}
        />
        <GraviButton
          disabled={!isOverrideReady}
          buttonText='Override'
          warning
          onClick={() =>
            overrideVolume(
              {
                override_volume: volumeOverrideValue,
                exclude_from_forecast: isExcluded,
                exclude_forecast_from_sales: excludeForecastValue,
              },
              data,
              column
            )
          }
        />
      </Horizontal>
    </Vertical>
  )
  const popTitle = <Texto category='h6'>{title} Override</Texto>

  function ellipsesText(event_name) {
    if (event_name?.length > 20) {
      return `${event_name.substring(0, 17).trim()}...`
    }
    return event_name
  }

  const detailPopContent = (
    <Vertical>
      <Horizontal className='p-2'>
        <Vertical flex={1}>
          <Texto>User:</Texto>
        </Vertical>
        <Vertical flex={1}>
          <Texto weight={600}>{override?.updated_by}</Texto>
        </Vertical>
      </Horizontal>
      {isToday && (
        <Horizontal className='p-2'>
          <Vertical flex={1}>
            <Texto>Updated At:</Texto>
          </Vertical>
          <Vertical flex={1}>
            <Texto weight={600}>{formatters.SHORT_DATE_TIME(override?.updated_on, storeTimeZone)}</Texto>
          </Vertical>
        </Horizontal>
      )}
      {override?.original_volume && (
        <Horizontal className='p-2'>
          <Vertical flex={1}>
            <Texto>Original Volume:</Texto>
          </Vertical>
          <Vertical flex={1}>
            <Texto weight={600}>{addCommasToNumber(override?.original_volume, 0)}</Texto>
          </Vertical>
        </Horizontal>
      )}
      {isToday && (
        <Horizontal className='p-2'>
          <Vertical flex={1}>
            <Texto>Overridden Volume:</Texto>
          </Vertical>
          <Vertical flex={1}>
            <Texto weight={600}> {addCommasToNumber(value, 0)}</Texto>
          </Vertical>
        </Horizontal>
      )}
      {column === 'sales' && (
        <Horizontal className='p-2'>
          <Vertical flex={1}>
            <Texto>Excluded from Forecast:</Texto>
          </Vertical>
          <Vertical flex={1}>
            <Texto weight={600}>{override?.exclude_from_forecast ? 'Yes' : 'No'}</Texto>
          </Vertical>
        </Horizontal>
      )}
      {override?.updated_on && (
        <Horizontal className='p-2'>
          <Vertical flex={1}>
            <Texto>Override Applied On:</Texto>
          </Vertical>
          <Vertical flex={1}>
            <Texto weight={600} style={{ textOverflow: 'ellipsis' }}>
              {formatters.SHORT_DATE_TIME(override?.updated_on, storeTimeZone)}
            </Texto>
          </Vertical>
        </Horizontal>
      )}
      {override?.event_id && (
        <Horizontal className='p-2'>
          <Vertical flex={1}>
            <Texto>Event:</Texto>
          </Vertical>
          <Vertical flex={1}>
            <Texto weight={600} style={{ textOverflow: 'ellipsis' }}>
              {ellipsesText(override?.event_name)}
            </Texto>
          </Vertical>
        </Horizontal>
      )}
      {override?.associated_override_id && (
        <Horizontal className='p-2'>
          <Vertical flex={1}>
            <Texto weight={600} className='text-danger'>
              This override affects future sales data and forecasts.
            </Texto>
          </Vertical>
        </Horizontal>
      )}
      {hasOverridePermission && override?.updated_by !== 'Outlier Detection' && (
        <Horizontal className='p-2'>
          <Popconfirm
            title='Are you sure you want to delete this override?'
            visible={deletePopoverVisible}
            onConfirm={() => deleteOverride(data, column)}
            onCancel={() => setDeletePopoverVisible(false)}
            okText='Yes'
            cancelText='No'
          >
            <GraviButton danger buttonText='Delete Override' onClick={() => setDeletePopoverVisible(true)} />
          </Popconfirm>
        </Horizontal>
      )}
    </Vertical>
  )
  const detailPopTitle = (
    <Horizontal justifyContent='space-between'>
      <Texto category='h6'>
        {formatters.DAY(data.date)} {formatters.DATE(data.date)} {title} Overridden
      </Texto>
    </Horizontal>
  )

  return (
    <Popover
      trigger='click'
      title={popTitle}
      content={popContent}
      visible={visiblePopoverId === `${column}-${data?.date}`}
    >
      {override !== null ? (
        <Popover trigger='hover' content={detailPopContent} title={detailPopTitle}>
          <GraviButton
            buttonText={buttonText}
            className='ghost-gravi-button'
            onClick={() => !hidePopover && setVisiblePopoverId(`${column}-${data?.date}`)}
            disabled={isManifoldChild}
          />
        </Popover>
      ) : (
        <GraviButton
          buttonText={buttonText}
          onClick={() => {
            if (!hidePopover) setVisiblePopoverId(`${column}-${data?.date}`)
          }}
          disabled={hidePopover || isManifoldChild}
          className='cursor-default bright-disabled ghost-gravi-button'
        />
      )}
    </Popover>
  )
}
